<script setup lang="ts">
import { computed, ref, watch } from 'vue';

const STEP_DEFAULT = 1;

type Props = {
  minValue?: number;
  maxValue?: number;
  step?: number;
  label: string;
  increaseLabel?: string;
  decreaseLabel?: string;
  initialValue?: number;
  optional?: boolean;
  maxMessage?: string;
  minMessage?: string;
  errorMessage?: string;
  helperText?: string;
  isWhite?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  step: STEP_DEFAULT,
  label: 'number input',
  increaseLabel: 'increase value',
  decreaseLabel: 'decrease value',
  minMessage: 'Min. allowed items',
  maxMessage: 'Max. allowed items',
  optional: false,
});

const emit = defineEmits(['onChange', 'ruleApplied']);

const validMaxValue = computed(
  () =>
    props.maxValue !== undefined && Number.isFinite(props.maxValue) && props.maxValue > 0
      ? props.maxValue
      : undefined,
);
const hasMaxLimit = computed(() => validMaxValue.value !== undefined);
const minValueRef = ref<number>(props.minValue ?? (props.optional ? 0 : 1));
const initialQuantity = Math.max(props.initialValue || minValueRef.value, minValueRef.value);
const initialStep = Number(props.step) || 1;
const value = ref<number>(
  initialStep > 1 ? Math.ceil(initialQuantity / initialStep) * initialStep : initialQuantity,
);
const hasError = ref<boolean>(false);

const nextMultiple = (quantity: number) => {
  const step = Number(props.step) || 1;
  return Math.ceil(quantity / step) * step;
};

const previousMultiple = (quantity: number) => {
  const step = Number(props.step) || 1;
  return Math.floor(quantity / step) * step;
};

const minimumMultiple = () => nextMultiple(minValueRef.value);
const maximumMultiple = () => {
  const maxValue = validMaxValue.value;
  return maxValue === undefined ? Number.MAX_SAFE_INTEGER : previousMultiple(maxValue);
};

const increase = () => {
  const nextValue = nextMultiple(value.value + 1);
  const maxValue = validMaxValue.value;
  if (maxValue === undefined || nextValue <= maxValue) {
    value.value = nextValue;
  }
  emit('onChange', value.value);
};

const decrease = () => {
  const previousValue = previousMultiple(value.value - 1);
  value.value = Math.max(previousValue, minimumMultiple());
  emit('onChange', value.value);
};

const validateValue = () => {
  const enteredValue = value.value;

  if (Number.isNaN(value.value)) {
    value.value = minValueRef.value;
  } else {
    const minimumValue = Math.max(value.value, minimumMultiple());
    const roundedValue = nextMultiple(minimumValue);
    const maxValue = validMaxValue.value;
    value.value =
      maxValue === undefined ? roundedValue : Math.min(roundedValue, maximumMultiple() || maxValue);

    if (enteredValue < minValueRef.value) {
      emit('ruleApplied', 'lowest');
    } else if (
      maxValue !== undefined &&
      (enteredValue > maxValue || roundedValue > maxValue)
    ) {
      emit('ruleApplied', 'highest');
    } else if (Number(props.step) > 1 && enteredValue % Number(props.step) !== 0) {
      emit('ruleApplied', 'multiple');
    }
  }

  hasError.value = false;
  emit('onChange', value.value);
};

watch(
  () => props.optional,
  (newValue) => {
    minValueRef.value = newValue ? 0 : 1;
    if (value.value < minValueRef.value) {
      value.value = minValueRef.value;
    }
  },
);

watch(
  () => props.minValue,
  (newValue) => {
    if (newValue) {
      minValueRef.value = newValue;
    }
  },
);
</script>

<template>
  <div :class="[$style.container, isWhite && $style.isWhite, hasError && $style.error]">
    <fieldset :class="[$style.fieldGroup, , value !== 0 && $style.hasValue]">
      <legend>{{ label }}</legend>

      <div :class="[$style.buttonContainer, value <= minimumMultiple() && $style.tooltipVisible]">
        <button
          :disabled="value <= minimumMultiple()"
          :name="decreaseLabel || 'decrease'"
          @click="decrease"
        >
          {{ 'remove' }}
        </button>
        <div v-if="minMessage" :class="$style.tooltip">
          <div :class="$style.tooltipMessage">{{ minMessage + ' ' + minimumMultiple() }}</div>
        </div>
      </div>

      <input
        v-model.number="value"
        :max="validMaxValue"
        :min="minimumMultiple()"
        :name="label"
        :step="step"
        :title="label"
        type="number"
        @blur="validateValue"
        @change="validateValue"
        @error="() => (hasError = true)"
      />
      <div
        v-if="hasMaxLimit"
        :class="[$style.buttonContainer, value >= maximumMultiple() && $style.tooltipVisible]"
      >
        <button
          :disabled="value >= maximumMultiple()"
          :name="increaseLabel || 'increase'"
          @click="increase"
        >
          {{ 'add' }}
        </button>
        <div v-if="maxMessage" :class="$style.tooltip">
          <div :class="$style.tooltipMessage">{{ maxMessage + ' ' + validMaxValue }}</div>
        </div>
      </div>
      <div v-else :class="$style.buttonContainer">
        <button :name="increaseLabel || 'increase'" @click="increase">
          {{ 'add' }}
        </button>
      </div>
    </fieldset>
    <p v-if="helperText" :class="$style.helperText">{{ helperText }}</p>
    <p v-if="hasError && errorMessage" :class="$style.errorText">{{ errorMessage }}</p>
  </div>
</template>

<style lang="sass" module src="./FormNumber.scss" />
